import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FitnessCenter as TrainerIcon,
  CalendarToday as CalendarIcon,
  Groups as GroupsIcon,
  ArrowBack as BackIcon,
  Videocam as VideocamIcon
} from '@mui/icons-material';
import { enUS, es } from 'date-fns/locale';
import groupSessionApi from 'api/graphql/groupSession';
import groupSessionSlice from 'store/modules/groupSession';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';
import { dateFormat } from 'common/constants/date';
import { ZOOM_ENABLED } from 'common/constants/features';
import { convertUtcToLocalTime } from 'common/utils/date';
import PillTabs from 'components/shared/tabs/PillTabs';
import SessionStatusChip from 'components/session/SessionStatusChip';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function PresenceToggle({ value = null, onChange }) {
  const { t } = useTranslation(['sessions']);
  return (
    <ButtonGroup size="small" variant="outlined">
      <Button
        onClick={() => onChange('PRESENT')}
        sx={value === 'PRESENT'
          ? { backgroundColor: (th) => th.palette.sessionStatus.completed.bg, color: (th) => th.palette.sessionStatus.completed.fg, borderColor: (th) => th.palette.sessionStatus.completed.fg, fontWeight: 700 }
          : { color: 'text.secondary', borderColor: 'hairline.strong' }}
      >
        {t('present', { ns: 'sessions', defaultValue: 'Presente' })}
      </Button>
      <Button
        onClick={() => onChange('ABSENT')}
        sx={value === 'ABSENT'
          ? { backgroundColor: (th) => th.palette.sessionStatus.absent.bg, color: (th) => th.palette.sessionStatus.absent.fg, borderColor: (th) => th.palette.sessionStatus.absent.fg, fontWeight: 700 }
          : { color: 'text.secondary', borderColor: 'hairline.strong' }}
      >
        {t('absentLabel', { ns: 'sessions', defaultValue: 'Ausente' })}
      </Button>
    </ButtonGroup>
  );
}
PresenceToggle.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired };

function CountPill({ color, label, value }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', p: 1, borderRadius: '12px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised' }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
  );
}
CountPill.propTypes = { color: PropTypes.string.isRequired, label: PropTypes.string.isRequired, value: PropTypes.number.isRequired };

/**
 * Panel de asistencia (admin/entrenador). El ENTRENADOR aparece primero (por si fue el
 * único), luego los clientes. Tabs Asistencia | Resumen. Monocromo + tokens semáforo.
 */
function GroupAttendancePanel({ session, onClose = null, onBack = null }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['sessions', 'labels', 'buttons']);
  const dateLocale = i18n.language === 'es' ? es : enUS;

  const [tab, setTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [trainerStatus, setTrainerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSaved(false);
    groupSessionApi.activePlusClients().then((response) => {
      const rawClients = response?.data?.users?.data ?? [];
      const sessionTime = session.start_date ? new Date(session.start_date).getTime() : null;
      // Excluir solo clientes creados DESPUÉS de la sesión. Si la fecha del cliente es inválida
      // o no parsea, NO se excluye (antes una fecha inválida los sacaba a todos → "0 inscritos").
      const rosterClients = sessionTime
        ? rawClients.filter((c) => {
          if (!c.created_at) return true;
          const created = new Date(c.created_at).getTime();
          return Number.isNaN(created) || created <= sessionTime;
        })
        : rawClients;

      const existingMap = {};
      (session.attendances ?? []).forEach((a) => {
        if (a.user?.id && a.status) existingMap[a.user.id] = a.status;
      });

      // Garantiza que CUALQUIER cliente ya marcado en la sesión aparezca, aunque no esté en el
      // roster de active plus (p. ej. cambió de plataforma): se une por id.
      const rosterIds = new Set(rosterClients.map((c) => String(c.id)));
      const attended = (session.attendances ?? [])
        .filter((a) => a.user?.id && !rosterIds.has(String(a.user.id)) && String(a.user.id) !== String(session.trainer?.id))
        .map((a) => a.user);
      const allClients = [...rosterClients, ...attended];
      setClients(allClients);
      const initial = {};
      allClients.forEach((c) => { initial[c.id] = existingMap[c.id] ?? null; });
      setAttendance(initial);

      if (session.trainer?.id) {
        const saved2 = existingMap[session.trainer.id];
        let trainerInit = null;
        if (saved2 === 'PRESENT' || saved2 === 'FINISHED') trainerInit = 'PRESENT';
        else if (saved2 === 'ABSENT') trainerInit = 'ABSENT';
        setTrainerStatus(trainerInit);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [session]);

  const handleChange = useCallback((userId, value) => {
    setAttendance((prev) => ({ ...prev, [userId]: value }));
    setSaved(false);
  }, []);

  const presentCount = clients.filter((c) => attendance[c.id] === 'PRESENT').length + (trainerStatus === 'PRESENT' ? 1 : 0);
  const absentCount = clients.filter((c) => attendance[c.id] === 'ABSENT').length + (trainerStatus === 'ABSENT' ? 1 : 0);
  const unmarkedCount = clients.filter((c) => !attendance[c.id]).length + (session.trainer && !trainerStatus ? 1 : 0);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.full_name?.toLowerCase().includes(q));
  }, [clients, search]);

  const handleZoomSync = useCallback(async () => {
    setSyncing(true);
    try {
      await groupSessionApi.syncZoomAttendance(session.id);
      // Refresca la lista (la sesión vuelve con asistencias actualizadas → el panel se reinicia).
      dispatch(groupSessionSlice.actions.markAttendanceSuccess());
    } catch {
      dispatch(groupSessionSlice.actions.markAttendanceError());
    } finally {
      setSyncing(false);
    }
  }, [session.id, dispatch]);

  const handleSave = useCallback(async () => {
    const clientAttendances = Object.entries(attendance)
      .map(([userId, st]) => ({ user_id: Number(userId), status: st || 'ABSENT' }));
    const trainerAttendances = session.trainer?.id
      ? [{ user_id: Number(session.trainer.id), status: trainerStatus || 'ABSENT' }]
      : [];
    const attendances = [...clientAttendances, ...trainerAttendances];

    // OJO: NO despachamos `markAttendance()` (sin payload) — eso disparaba el saga homónimo con
    // variables undefined → mutación inválida → toast de error VACÍO, mientras esta llamada directa
    // sí guardaba. Hacemos SOLO la llamada directa (con datos) y mostramos el feedback aquí.
    setIsSaving(true);
    try {
      await groupSessionApi.markAttendance({ group_session_id: session.id, attendances });
      setSaved(true);
      dispatch(groupSessionSlice.actions.markAttendanceSuccess());
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `mark-attendance-ok-${session.id}`,
        variant: 'success',
        message: t('attendanceSaved', { ns: 'sessions', defaultValue: 'Asistencia guardada' })
      })));
    } catch {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `mark-attendance-err-${session.id}`,
        variant: 'error',
        message: t('attendanceSaveError', { ns: 'sessions', defaultValue: 'No se pudo guardar la asistencia' })
      })));
    } finally {
      setIsSaving(false);
    }
  }, [attendance, trainerStatus, session, dispatch]);

  const dayName = capitalize(convertUtcToLocalTime(session.start_date, 'EEEE', dateLocale));
  const dateLabel = convertUtcToLocalTime(session.start_date, dateFormat, dateLocale);
  const enrolled = clients.length;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {onBack && (
            <IconButton size="small" onClick={onBack} aria-label="back" sx={{ color: 'text.secondary' }}>
              <BackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('sessionOf', { ns: 'sessions', defaultValue: 'Sesión del' })} {dayName.toLowerCase()}
          </Typography>
          <SessionStatusChip status={session.status} startDate={session.start_date} />
        </Stack>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="close" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <CalendarIcon sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{dateLabel}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <GroupsIcon sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {enrolled} {t('enrolled', { ns: 'sessions', defaultValue: 'inscritos' })}
          </Typography>
        </Stack>
        {session.trainer && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TrainerIcon sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{session.trainer.full_name}</Typography>
          </Stack>
        )}
      </Stack>

      {/* El panel vive en un drawer de 400px: la píldora ocupa el ancho completo para que
          las dos opciones se repartan la fila sin scroll horizontal. */}
      <PillTabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        fullWidth
        ariaLabel={t('attendanceTabsLabel', { ns: 'sessions', defaultValue: 'Secciones de asistencia' })}
        sx={{ mb: 1.5 }}
        tabs={[
          { key: 'attendance', label: t('attendanceTab', { ns: 'sessions', defaultValue: 'Asistencia' }) },
          { key: 'summary', label: t('summaryTab', { ns: 'sessions', defaultValue: 'Resumen' }) }
        ]}
      />

      {ZOOM_ENABLED && session.zoom_meeting_id && (
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <VideocamIcon />}
          onClick={handleZoomSync}
          disabled={syncing}
          sx={{ mb: 2, color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700 }}
        >
          {t('syncZoom', { ns: 'sessions', defaultValue: 'Sincronizar asistencia (Zoom)' })}
        </Button>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: 'secondary.main' }} />
        </Box>
      ) : (
        <>
          {/* Counts */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <CountPill color={(th) => th.palette.sessionStatus.completed.fg} label={t('presentPlural', { ns: 'sessions', defaultValue: 'Presentes' })} value={presentCount} />
            <CountPill color={(th) => th.palette.sessionStatus.absent.fg} label={t('absentPlural', { ns: 'sessions', defaultValue: 'Ausentes' })} value={absentCount} />
            <CountPill color={(th) => th.palette.text.secondary} label={t('unmarked', { ns: 'sessions', defaultValue: 'Sin marcar' })} value={unmarkedCount} />
          </Stack>

          {tab === 0 && (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder={t('searchClient', { ns: 'sessions', defaultValue: 'Buscar cliente' })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />

              {/* ENTRENADOR primero */}
              {session.trainer && !search && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    {t('trainer', { count: 1, ns: 'labels', defaultValue: 'Entrenador' })}
                  </Typography>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar src={session.trainer.photo} alt={session.trainer.full_name} sx={{ width: 30, height: 30, fontSize: '0.78rem' }}>
                        {session.trainer.full_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{session.trainer.full_name}</Typography>
                        <Typography variant="caption" sx={{ color: 'secondary.main' }}>
                          {t('trainer', { count: 1, ns: 'labels', defaultValue: 'Entrenador' })}
                        </Typography>
                      </Box>
                    </Stack>
                    <PresenceToggle value={trainerStatus} onChange={(v) => { setTrainerStatus(v); setSaved(false); }} />
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              {/* Clientes */}
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, display: 'block', mb: 0.5 }}>
                {t('clientsLabel', { ns: 'sessions', defaultValue: 'Clientes' })}
              </Typography>
              <Box sx={{ maxHeight: { md: 360 }, overflowY: { md: 'auto' } }}>
                {filteredClients.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {t('noClientsFilter', { ns: 'sessions', defaultValue: 'No hay clientes con este filtro' })}
                  </Typography>
                ) : filteredClients.map((client) => (
                  <Stack key={client.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1, borderBottom: '1px solid', borderColor: 'hairline.main', '&:last-child': { borderBottom: 'none' } }}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar src={client.photo} alt={client.full_name} sx={{ width: 28, height: 28, fontSize: '0.74rem' }}>
                        {client.full_name?.[0]}
                      </Avatar>
                      <Typography variant="body2" noWrap>{client.full_name}</Typography>
                    </Stack>
                    <PresenceToggle value={attendance[client.id]} onChange={(v) => handleChange(client.id, v)} />
                  </Stack>
                ))}
              </Box>
            </>
          )}

          {tab === 1 && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {t('summaryTab', { ns: 'sessions', defaultValue: 'Resumen' })}
              </Typography>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">{t('enrolled', { ns: 'sessions', defaultValue: 'Inscritos' })}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{enrolled}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">{t('presentPlural', { ns: 'sessions', defaultValue: 'Presentes' })}</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: (th) => th.palette.sessionStatus.completed.fg }}>{presentCount}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">{t('absentPlural', { ns: 'sessions', defaultValue: 'Ausentes' })}</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: (th) => th.palette.sessionStatus.absent.fg }}>{absentCount}</Typography></Stack>
              </Stack>
            </Box>
          )}

          {/* Acciones */}
          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
            <Button fullWidth variant="outlined" color="inherit" onClick={onClose} sx={{ color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700 }}>
              {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {isSaving ? t('saving', { ns: 'sessions', defaultValue: 'Guardando...' }) : (saved ? t('saved', { ns: 'sessions', defaultValue: 'Guardado ✓' }) : t('saveAttendance', { ns: 'sessions', defaultValue: 'Guardar asistencia' }))}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

GroupAttendancePanel.propTypes = {
  session: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onBack: PropTypes.func
};

export default GroupAttendancePanel;
